import { useEffect, useRef } from 'react';
import { tap } from './playerHelpers';

// Right-swipe the sheet itself, revealing the existing Home underneath.
// Forms and the nested social picker keep their own gestures. Buttons remain
// tappable; an intentional drag captures the pointer and suppresses its click.
export default function useSupportSwipeBack(onBack, disabled = false) {
  const shell = useRef(null), back = useRef(onBack);
  back.current = onBack;
  useEffect(() => {
    const element = shell.current;
    if (!element || disabled) return undefined;
    const sheet = element.closest('[role="dialog"]') || element;
    let gesture = null, timer = 0, suppressUntil = 0;
    const overlay = sheet.previousElementSibling?.classList.contains('bg-black/80') ? sheet.previousElementSibling : null;
    const originalTransform = sheet.style.transform, originalTransition = sheet.style.transition, originalAnimation = sheet.style.animation;
    const originalOpacity = overlay?.style.opacity || '';
    const reset = () => {
      clearTimeout(timer); gesture = null;
      sheet.style.transform = originalTransform; sheet.style.transition = originalTransition; sheet.style.animation = originalAnimation;
      if (overlay) overlay.style.opacity = originalOpacity;
    };
    const down = event => {
      if (gesture || event.isPrimary === false || event.button > 0 || event.clientX < 18
        || event.target.closest('input,textarea,select,[contenteditable="true"],[data-no-swipe]')) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), width: sheet.clientWidth, distance: 0,
        reduced: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches };
    };
    const move = event => {
      const g = gesture;
      if (!g || g.settling || g.id !== event.pointerId) return;
      const dx = event.clientX - g.x, dy = event.clientY - g.y;
      if (!g.locked) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx) || dx < -10) { reset(); return; }
        if (dx < 12 || dx < Math.abs(dy) * 1.3) return;
        g.locked = true; element.setPointerCapture?.(event.pointerId);
        sheet.style.transition = 'none';
        sheet.style.animation = 'none';
      }
      event.preventDefault();
      g.distance = Math.max(0, Math.min(g.width, dx));
      if (!g.reduced) {
        sheet.style.transform = `translate3d(${g.distance}px,0,0)`;
        if (overlay) overlay.style.opacity = String(1 - g.distance / g.width);
      }
    };
    const finish = event => {
      const g = gesture;
      if (!g || g.settling || g.id !== event.pointerId) return;
      if (!g.locked) { reset(); return; }
      const commit = event.type === 'pointerup' && (g.distance > g.width * .28
        || g.distance > 28 && g.distance / Math.max(1, performance.now() - g.time) > .5);
      suppressUntil = Date.now() + 450; g.settling = true;
      const complete = () => { if (commit && back.current) { tap(10); back.current(); gesture = null; } else reset(); };
      if (g.reduced) { complete(); return; }
      sheet.style.transition = 'transform 180ms cubic-bezier(.23,1,.32,1)';
      sheet.style.transform = `translate3d(${commit ? g.width : 0}px,0,0)`;
      timer = setTimeout(complete, 190);
    };
    const click = event => { if (Date.now() < suppressUntil) { event.preventDefault(); event.stopImmediatePropagation(); } };
    element.addEventListener('pointerdown', down);
    element.addEventListener('pointermove', move, { passive: false });
    element.addEventListener('pointerup', finish); element.addEventListener('pointercancel', finish);
    element.addEventListener('click', click, true);
    window.addEventListener('blur', reset); window.addEventListener('resize', reset);
    return () => {
      reset(); element.removeEventListener('pointerdown', down); element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', finish); element.removeEventListener('pointercancel', finish);
      element.removeEventListener('click', click, true); window.removeEventListener('blur', reset); window.removeEventListener('resize', reset);
    };
  }, [disabled]);
  return shell;
}
